import React, { useEffect, useState } from 'react';
import { resolveAsset } from '../api/client';
import TextBubble from '../lib/textBubble';
import { isolateBidi, storyTextDir } from '../lib/bidi';
import { sceneTextLayout } from '../lib/sceneText';
import { storyFontCss } from '../lib/storyFonts';
import { useT } from '../i18n';

const FLIP_MS = 700;

// Split a title into balanced lines for the painted (two-color, line-alternating)
// cover title: 1-2 words → one line; 3-4 → two lines; 5+ → three. Distributes
// words as evenly as possible so the stacked lines look intentional.
export function splitTitleLines(title) {
  const words = String(title || '').trim().split(/\s+/).filter(Boolean);
  if (words.length <= 2) return [words.join(' ')] .filter(Boolean);
  const lines = words.length <= 4 ? 2 : 3;
  const per = Math.ceil(words.length / lines);
  const out = [];
  for (let i = 0; i < words.length; i += per) out.push(words.slice(i, i + per).join(' '));
  return out;
}

// Perceived luminance (0–255) of a #rrggbb color — picks a dark vs white outline.
function hexLuminance(hex) {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex || '');
  if (!m) return 0;
  const n = parseInt(m[1], 16);
  return 0.299 * ((n >> 16) & 255) + 0.587 * ((n >> 8) & 255) + 0.114 * (n & 255);
}

// Build a THICK, ROUNDED text outline from a ring of layered shadows instead of
// `-webkit-text-stroke` (whose miter joins shoot ugly spikes off sharp letter
// corners, with no CSS way to round them). Stacking glyph copies offset around
// several concentric rings is a Minkowski-sum-with-a-disc → a smooth rounded
// border, no spikes. Offsets are in `em` so the outline scales with font-size.
// A soft dark drop-shadow at the end lifts the title off the artwork. `width`
// (em) is the OUTER ring radius ≈ the outline thickness; the inner rings scale
// with it so a thinner/thicker border stays evenly filled.
const DEFAULT_OUTLINE_WIDTH = 0.1;
function paintedOutline(color, width) {
  const w = Number.isFinite(width) && width >= 0 ? width : DEFAULT_OUTLINE_WIDTH;
  const rings = [0.25, 0.5, 0.75, 1].map((f) => +(f * w).toFixed(4));
  const dirs = 16;
  const parts = [];
  for (const r of rings) {
    if (r <= 0) continue;
    for (let i = 0; i < dirs; i++) {
      const a = (Math.PI * 2 * i) / dirs;
      parts.push(`${(Math.cos(a) * r).toFixed(3)}em ${(Math.sin(a) * r).toFixed(3)}em 0 ${color}`);
    }
  }
  parts.push('0 0.06em 0.07em rgba(0,0,0,0.32)');
  return parts.join(', ');
}

// Curated Arabic display fonts offered in the cover-title designer. `css` is the
// full font-family stack; `label` is the Arabic name shown in the picker. Keep
// this list IN SYNC with server/services/bookPdf.js COVER_FONTS and the Google
// Fonts <link> in client/public/index.html + bookPdf.js htmlDoc(). The first
// entry (expo) is the default painted-title font (matches the .cover-title--
// painted CSS); Expo Arabic is self-hosted (@font-face in App.css) and falls
// back to Cairo when its file is absent.
export const COVER_FONTS = [
  { key: 'expo',    label: 'إكسبو عربي (افتراضي)', css: "'Expo Arabic', 'Cairo', sans-serif" },
  { key: 'cairo',   label: 'كايرو',           css: "'Cairo', sans-serif" },
  { key: 'baloo',   label: 'بالو',            css: "'Baloo Bhaijaan 2', 'Cairo', sans-serif" },
  { key: 'changa',  label: 'شنغا',            css: "'Changa', 'Cairo', sans-serif" },
  { key: 'messiri', label: 'المسيري',         css: "'El Messiri', 'Cairo', serif" },
  { key: 'lalezar', label: 'لاله‌زار',        css: "'Lalezar', 'Cairo', cursive" },
  { key: 'rakkas',  label: 'ركّاس',           css: "'Rakkas', 'Cairo', cursive" },
  { key: 'reem',    label: 'ريم كوفي',        css: "'Reem Kufi', 'Cairo', sans-serif" },
  { key: 'tajawal', label: 'تجوال',           css: "'Tajawal', 'Cairo', sans-serif" },
];
export function coverFontCss(key) {
  const f = COVER_FONTS.find((x) => x.key === key);
  return f ? f.css : null;
}

// Resolve the inline style for ONE painted title line, merging the admin's
// per-line override (coverTitleStyle.lines[i]) over the auto-sampled palette
// defaults. Shared shape with server/services/bookPdf.js resolveTitleLineStyle —
// keep the two in lock-step so screen + PDF render identically.
function resolveLineStyle(i, override, titleColors) {
  const o = override || {};
  const c = titleColors || {};
  const base = i % 2 === 0 ? c.primary : (c.secondary || c.primary);
  const fill = o.fill || base || '#f5f5f5';
  const outline = o.outline || (hexLuminance(fill) > 178 ? '#21314f' : '#ffffff');
  const width = Number.isFinite(o.outlineWidth) ? o.outlineWidth : DEFAULT_OUTLINE_WIDTH;
  const style = { color: fill, textShadow: paintedOutline(outline, width) };
  const font = coverFontCss(o.font);
  if (font) style.fontFamily = font;
  if (Number.isFinite(o.fontSize) && o.fontSize > 0) style.fontSize = `${o.fontSize}pt`;
  if (Number.isFinite(o.letterSpacing)) style.letterSpacing = `${o.letterSpacing}em`;
  if (Number.isFinite(o.weight) && o.weight > 0) style.fontWeight = o.weight;
  return style;
}

// Inline position override for an admin-dragged cover title. `position` is
// {x,y} as percentages of the cover panel — x is the horizontal CENTRE, y the
// TOP edge — so translateX(-50%) keeps it anchored top-centre exactly like the
// stylesheet default. Returns undefined when unset so the CSS default position
// (top band, centred) wins and legacy stories render unchanged.
export function coverTitleStyle(position) {
  if (!position || !Number.isFinite(position.x) || !Number.isFinite(position.y)) return undefined;
  return { left: `${position.x}%`, top: `${position.y}%`, transform: 'translateX(-50%)' };
}

// The cover title as it renders on the real book: when the cover yielded a
// two-color palette (`titleColors.primary`), paint it as the chunky, line-
// alternating, thick-outlined kids' title (splitTitleLines + paintedOutline);
// otherwise fall back to the plain themed plate title. `position` (optional)
// pins it where the admin dragged it. `rootProps` (optional) is spread onto the
// title element so the admin preview can make it a drag handle — the title is
// absolutely positioned, so a wrapping element would collapse and never receive
// the pointer events. Exported so the admin cover-preview (AdminStory) shows the
// IDENTICAL title to the book/PDF instead of the old plain style.
export function CoverTitle({ title, titleColors, titleTheme, titleStyle, position, rootProps, onLineClick, selectedLine }) {
  if (!title) return null;
  const posStyle = coverTitleStyle(position);
  const extraClass = rootProps?.className ? ` ${rootProps.className}` : '';
  const { className: _ignored, ...restProps } = rootProps || {};
  const lineStyles = Array.isArray(titleStyle?.lines) ? titleStyle.lines : [];
  const hasLineStyles = lineStyles.some((l) => l && Object.keys(l).length > 0);
  const maxWidthPct = Number.isFinite(titleStyle?.maxWidthPct) ? titleStyle.maxWidthPct : null;
  const lineGap = Number.isFinite(titleStyle?.lineGap) ? titleStyle.lineGap : null;
  const maxWidthCss = maxWidthPct != null ? `${maxWidthPct}%` : null;
  // The title carries NO scaling of its own: it always lays out at full
  // print-panel pixel size (font in pt, % positions/widths against its panel-
  // sized container). The admin preview shrinks it to the small box by wrapping
  // it in a transform-scaled "stage" of the exact panel dimensions — so its
  // shrink-to-fit width and line wrapping are IDENTICAL to the PDF. (Scaling the
  // title alone while its container stayed box-sized squeezed the available
  // width and broke a 2-word painted line into one word per line.)
  // Per-line span props: data-line lets the admin preview map a click back to a
  // line; onLineClick + selectedLine power the in-context popover editor. Both
  // are no-ops in the real book viewer (props unset).
  const spanProps = (i) => {
    const p = { 'data-line': i };
    if (onLineClick) {
      p.onClick = (e) => { e.stopPropagation(); onLineClick(i, e); };
      p.style = { cursor: 'pointer', outline: selectedLine === i ? '2px dashed rgba(99,102,241,0.9)' : undefined, outlineOffset: '2px', borderRadius: '4px' };
    }
    return p;
  };
  const mergeSpanStyle = (base, i) => {
    const sp = spanProps(i);
    return { ...sp, style: { ...base, ...(sp.style || {}) } };
  };
  // Paint the chunky line-alternating title when the cover yielded a palette OR
  // the admin set any per-line style; otherwise the plain themed plate title.
  if (titleColors?.primary || hasLineStyles) {
    const rootStyle = { ...(posStyle || {}) };
    if (maxWidthCss) rootStyle.maxWidth = maxWidthCss;
    if (lineGap != null) rootStyle.gap = `${lineGap}em`;
    return (
      <h1
        className={`cover-title cover-title--painted${extraClass}`}
        style={Object.keys(rootStyle).length ? rootStyle : undefined}
        {...restProps}
      >
        {splitTitleLines(title).map((line, i) => (
          <span key={i} className="cover-title-line" {...mergeSpanStyle(resolveLineStyle(i, lineStyles[i], titleColors), i)}>
            {line}
          </span>
        ))}
      </h1>
    );
  }
  const plainStyle = { ...(posStyle || {}) };
  if (maxWidthCss) plainStyle.maxWidth = maxWidthCss;
  return (
    <h1
      className={`cover-title cover-title--${titleTheme || 'dark'}${extraClass}`}
      style={Object.keys(plainStyle).length ? plainStyle : undefined}
      {...restProps}
    >
      {title}
    </h1>
  );
}

// Bucket scene text by length to pick a font-size class. Exported so the admin
// scene editor's exact-print preview picks the SAME auto class as the book/PDF.
export function sceneTextSizeClass(text) {
  const n = (text || '').length;
  if (n > 320) return 'scene-text--xlong';
  if (n > 200) return 'scene-text--long';
  return '';
}

// An admin per-scene override is a pt tuned for the full-size print panel. In the
// small on-screen spread we must NOT apply it literally (it would be sized for the
// print page and overflow), and the customer preview should show the text at the
// SAME proportion it actually prints at. The PDF sets 16pt on a 200mm text page
// (a 16pt glyph = 2.82% of that page); that page is half of this two-panel spread,
// so the print-faithful on-screen size is 1.41cqw for 16pt. We scale that by
// (pt / 16) so every override renders true-to-print — a large value like 28pt sits
// at ~half the page just like the PDF instead of ballooning to fill it. This only
// affects the on-screen preview; the printed PDF (server bookPdf.js) and the admin
// editor keep literal pt and are unchanged. (Auto-sized scenes are a touch larger,
// via the CSS `.scene-text` clamp, for on-screen readability — that's intentional.)
const PDF_SCENE_BASE_PT = 16;
export function scenePreviewFontSize(pt) {
  const r = Number(pt) / PDF_SCENE_BASE_PT;
  if (!Number.isFinite(r) || r <= 0) return `${pt}pt`;
  const round = (n) => +n.toFixed(2);
  return `clamp(${round(8 * r)}px, ${round(1.41 * r)}cqw, ${round(15 * r)}px)`;
}

// ─── Back-cover helpers ──────────────────────────────────────────────────────
// Both Preview.jsx and FinalizeOrder.jsx call buildBackPage() to push the
// final page onto their pages array. The same helper lives here so the
// back-cover rendering and its data shape stay in lock-step with the Page
// switch below. Server returns the form values on `story.personalization`.

// Gendered dedication: Arabic uses البطل (m) / البطلة (f). The form sets
// gender to ولد/بنت; we also accept English/French-style values defensively.
// Exported so the admin editor can show this auto-derived default as the
// placeholder for the (optional) hand-written dedication override.
export function buildDedication(personalization, t) {
  const name = (personalization?.name || '').trim();
  const g = String(personalization?.gender || '');
  const isGirl =
    g.includes('بنت') ||
    g.includes('أنثى') ||
    g.toLowerCase().startsWith('f') ||
    g.toLowerCase() === 'girl';
  // English story → English dedication line (the templated pieces aren't
  // LLM-generated, so they're localized here rather than translated).
  if (storyTextDir(personalization) === 'ltr') {
    const who = isGirl ? 'heroine' : 'hero';
    return name ? `To ${name}, our little ${who}` : `To our little ${who}`;
  }
  if (!name) return isGirl
    ? (t?.dedicationTitleGirl || 'إلى بطلتنا الصغيرة')
    : (t?.dedicationTitleBoy || 'إلى بطلنا الصغير');
  return isGirl
    ? (t?.dedicationTitleNameGirl || 'إلى {name} البطلة').replace('{name}', name)
    : (t?.dedicationTitleNameBoy || 'إلى {name} البطل').replace('{name}', name);
}

// Auto-derived back-cover blurb for stories without an LLM-written or
// admin-edited summary. Deliberately does NOT splice the raw form goal into
// the sentence (it produced awkward copy like "قصة نيل ورحلته الصغيرة: ان لا
// يضرب.") — instead it's warm, encouraging jacket copy built from the child's
// name and gender. Mirrors server/services/bookPdf.js buildSummary.
export function buildSummary(personalization, t) {
  const name = (personalization?.name || '').trim();
  const g = String(personalization?.gender || '');
  const isGirl =
    g.includes('بنت') || g.includes('أنثى') || g.toLowerCase().startsWith('f') || g.toLowerCase() === 'girl';
  if (storyTextDir(personalization) === 'ltr') {
    const who = isGirl ? 'heroine' : 'hero';
    const pron = isGirl ? 'her' : 'him';
    return name
      ? `In this story, ${name} sets off on a little adventure full of warmth and imagination, discovers ${isGirl ? 'her' : 'his'} own strength, and learns that courage grows step by step.\nA story written and illustrated just for ${pron} — to be the ${who}.`
      : `In this story, our little ${who} sets off on an adventure full of warmth and imagination, discovers ${isGirl ? 'her' : 'his'} own strength, and learns that courage grows step by step.\nA story written and illustrated just for ${pron}.`;
  }
  if (name) {
    return isGirl
      ? (t?.summaryNameGirl || 'في هذه الحكاية، تنطلق {name} في مغامرة صغيرة مليئة بالدفء والخيال، تكتشف فيها قوّتها الخاصة، وتتعلّم أن الشجاعة تكبر معها خطوة بعد خطوة.\nقصة كُتبت ورُسمت خصيصاً لها — لتكون هي البطلة.').replace('{name}', name)
      : (t?.summaryNameBoy || 'في هذه الحكاية، ينطلق {name} في مغامرة صغيرة مليئة بالدفء والخيال، يكتشف فيها قوّته الخاصة، ويتعلّم أن الشجاعة تكبر معه خطوة بعد خطوة.\nقصة كُتبت ورُسمت خصيصاً له — ليكون هو البطل.').replace('{name}', name);
  }
  return isGirl
    ? (t?.summaryNoNameGirl || 'في هذه الحكاية، تنطلق بطلتنا الصغيرة في مغامرة مليئة بالدفء والخيال، تكتشف فيها قوّتها الخاصة، وتتعلّم أن الشجاعة تكبر معها خطوة بعد خطوة.\nقصة كُتبت ورُسمت خصيصاً لها.')
    : (t?.summaryNoNameBoy || 'في هذه الحكاية، ينطلق بطلنا الصغير في مغامرة مليئة بالدفء والخيال، يكتشف فيها قوّته الخاصة، ويتعلّم أن الشجاعة تكبر معه خطوة بعد خطوة.\nقصة كُتبت ورُسمت خصيصاً له.');
}

export function buildBackPage(story, t) {
  const p = story?.personalization || {};
  // Admin-saved back-cover text overrides the auto defaults; empty falls back.
  return {
    kind: 'back',
    summary: (story?.summary || '').trim() || buildSummary(p, t),
    dedication: (story?.dedication || '').trim() || buildDedication(p, t),
    // Back panel of the wraparound cover spread (the sparse continuation of the
    // front artwork). Null on legacy stories → plain cream back page.
    backImageUrl: story?.coverBackImageUrl || null,
  };
}

// Keepsake paragraph for the dedication page — the last page INSIDE the book,
// written to be reread when the child is older. Gendered, name-agnostic (the
// name lives in the dedication line above it). Mirrors server/services/
// bookPdf.js buildDedicationText.
export function buildDedicationText(personalization, t) {
  const g = String(personalization?.gender || '');
  const isGirl =
    g.includes('بنت') || g.includes('أنثى') || g.toLowerCase().startsWith('f') || g.toLowerCase() === 'girl';
  if (storyTextDir(personalization) === 'ltr') {
    const who = isGirl ? 'heroine' : 'hero';
    return `This story was written and illustrated just for you — there is no other copy of it anywhere in the world.\nKeep it close, and whenever you grow older and return to its pages, remember that the courage never came from the story — it was inside you all along.\nAlways be the ${who} of your own story.`;
  }
  return isGirl
    ? (t?.dedicationTextGirl || 'هذه القصة كُتبت ورُسمت خصيصاً لكِ، ولا توجد نسخة أخرى منها في العالم كلّه.\nاحتفظي بها جيداً، وكلما كبرتِ وعدتِ إلى صفحاتها، تذكّري أن الشجاعة لم تأتِ من الحكاية — بل كانت فيكِ منذ البداية.\nكوني دائماً بطلة حكايتكِ.')
    : (t?.dedicationTextBoy || 'هذه القصة كُتبت ورُسمت خصيصاً لك، ولا توجد نسخة أخرى منها في العالم كلّه.\nاحتفظ بها جيداً، وكلما كبرت وعدت إلى صفحاتها، تذكّر أن الشجاعة لم تأتِ من الحكاية — بل كانت فيك منذ البداية.\nكن دائماً بطل حكايتك.');
}

// The dedication page: faces the child's storybook portrait (character ref)
// when one exists; the year stamps when the book was made.
export function buildDedicationPage(story, t) {
  const p = story?.personalization || {};
  const created = story?.createdAt ? new Date(story.createdAt) : null;
  return {
    kind: 'dedication',
    title: (story?.dedication || '').trim() || buildDedication(p, t),
    text: (story?.dedicationText || '').trim() || buildDedicationText(p, t),
    year: created && !Number.isNaN(created.getTime()) ? created.getFullYear() : null,
    portraitUrl: story?.characterRefUrl || p.characterImageUrl || null,
    portraitTransform: story?.dedicationImageTransform || null,
  };
}

// Inline style that pans (object-position) + zooms (scale) the dedication
// portrait inside its fixed circular frame. MUST stay in sync with the server's
// dedicationPortraitStyleAttr() in bookPdf.js so screen and print match exactly.
export function dedicationPortraitStyle(transform) {
  const t = transform || {};
  const posX = Number.isFinite(t.posX) ? t.posX : 50;
  const posY = Number.isFinite(t.posY) ? t.posY : 50;
  const zoom = Number.isFinite(t.zoom) ? t.zoom : 1;
  return {
    objectPosition: `${posX}% ${posY}%`,
    transform: `scale(${zoom})`,
    transformOrigin: `${posX}% ${posY}%`,
  };
}

// Full, unlocked book: front cover + every scene (with its illustration) + back
// cover. Shared by the post-payment views (finalize, order page) and the PDF
// export so the on-screen book and the downloaded file stay identical.
export function buildFullBookPages(story, t) {
  const pages = [{
    kind: 'front',
    title: story.title,
    imageUrl: story.coverImageUrl || null,
    titleTheme: story.coverTitleTheme === 'light' ? 'light' : 'dark',
    titleColors: story.coverTitleColors || null,
    titleStyle: story.coverTitleStyle || null,
    titlePosition: story.coverTitlePosition || null,
  }];
  // Story-text direction: an English story (hand-typed or generated) reads LTR;
  // everything else (Arabic/Hebrew) keeps the RTL base. Set on the text container
  // per scene so the physical RTL binding/layout is untouched — only text flows.
  const textDir = storyTextDir(story?.personalization);
  (story.scenes || []).forEach((s, i) => {
    // Per-scene admin overrides (direction / alignment / dragged position) resolve
    // against the story's base direction. Unset scenes keep the legacy defaults.
    const layout = sceneTextLayout(s, textDir);
    pages.push({
      kind: 'scene', text: s.text, dir: layout.dir, align: layout.align,
      positioned: layout.positioned, posX: layout.x, posY: layout.y,
      imageUrl: s.imageUrl, sceneIndex: i, fontSize: s.fontSize || null, textBubble: !!story.textBubble,
    });
  });
  // Last page INSIDE the book: the keepsake dedication, then the closed back.
  pages.push(buildDedicationPage(story, t));
  pages.push(buildBackPage(story, t));
  return pages;
}

// ─── Physical-book presentation ──────────────────────────────────────────────
// The preview mimics the real hardcover: the first page is the CLOSED book
// (front board, spine on the right — RTL binding), every story beat is the
// OPEN book (two facing pages: one illustration page, one text page, with a
// center gutter), and the last page is the closed book seen from the back
// (back board with the summary panel + brand). Covers shown here are the
// trimmed 20×21cm visible faces — the print files carry the extra 2cm wrap.

// Closed-book board. `spine` is the bound edge ('right' for the front of an
// RTL book, 'left' for its back); the page block peeks out of the opposite
// (fore) edge.
function BookBoard({ side, children, contain }) {
  return (
    <div className={`book-board book-board--${side}`}>
      {children}
      <span className={`board-spine board-spine--${side === 'front' ? 'right' : 'left'}`} aria-hidden="true" />
      <span className={`board-pages board-pages--${side === 'front' ? 'left' : 'right'}`} aria-hidden="true" />
    </div>
  );
}

function LockedTeaser() {
  const t = useT();
  return (
    <div className="locked-overlay locked-overlay--cta">
      <span className="locked-icon">&#128274;</span>
      <p className="locked-text">{t.previewLockedScene}</p>
      <p className="locked-cta-text">{t.previewLockedEditable}</p>
    </div>
  );
}

export function Page({ page, titleDrag }) {
  const t = useT();
  if (!page) return <div className="page page-blank" aria-hidden="true" />;

  if (page.kind === 'front') {
    // In the customer preview the title doubles as a drag handle: titleDrag
    // carries the live dragged position (overriding the saved one) plus the
    // pointer-down starter. Absent (paid book / PDF) → plain, non-draggable.
    const editable = !!titleDrag;
    const titlePos = (editable && titleDrag.position) || page.titlePosition || null;
    return (
      <div className="page page-book">
        <BookBoard side="front">
          {page.imageUrl ? (
            // Always fill the board edge-to-edge (object-fit: cover). We used to
            // flip to object-fit:contain for landscape covers once the image
            // loaded, but that flip made the cover visibly JUMP on load and
            // letterboxed wide covers with empty cream bands. Filling keeps it
            // steady; any over-wide legacy cover is cropped, not bordered.
            <img
              className="board-cover-img"
              src={resolveAsset(page.imageUrl)}
              alt=""
            />
          ) : (
            <div className="board-cover-img cover-image-fallback" aria-hidden="true" />
          )}
          <CoverTitle
            title={page.title}
            titleColors={page.titleColors}
            titleTheme={page.titleTheme}
            titleStyle={page.titleStyle}
            position={titlePos}
            rootProps={editable ? { className: 'is-title-draggable', onPointerDown: titleDrag.onPointerDown } : undefined}
          />
        </BookBoard>
      </div>
    );
  }

  if (page.kind === 'back') {
    return (
      <div className="page page-book">
        <BookBoard side="back">
          {page.backImageUrl && (
            <img className="board-cover-img" src={resolveAsset(page.backImageUrl)} alt="" />
          )}
          <div className="back-box">
            <p className="back-summary">{page.summary}</p>
            <div className="back-divider" aria-hidden="true" />
            <p className="back-dedication">{page.dedication}</p>
          </div>
          <div className="back-brand">
            <img className="back-logo" src="/logo.png" alt={t.appTitle} />
            <div className="back-site">anabatal.com</div>
          </div>
        </BookBoard>
      </div>
    );
  }

  if (page.kind === 'locked') {
    return (
      <div className="page page-book">
        <div className="book-spread book-spread--locked">
          <div className="leaf leaf--text" />
          <div className="leaf leaf--image" />
          <span className="spread-gutter" aria-hidden="true" />
          <LockedTeaser />
        </div>
      </div>
    );
  }

  if (page.kind === 'dedication') {
    // The keepsake page: the child's storybook portrait (medallion) on the
    // right page, the dedication on the left — the last opening of the book.
    return (
      <div className="page page-book">
        <div className="book-spread book-spread--image-right book-spread--dedication">
          <div className="leaf leaf--image leaf--portrait">
            {page.portraitUrl ? (
              <div className="dedication-portrait">
                <img
                  className="dedication-portrait-img"
                  src={resolveAsset(page.portraitUrl)}
                  alt=""
                  style={dedicationPortraitStyle(page.portraitTransform)}
                />
              </div>
            ) : (
              <div className="dedication-ornament" aria-hidden="true" />
            )}
          </div>
          <div className="leaf leaf--text leaf--dedication">
            <div className="back-divider" aria-hidden="true" />
            <h2 className="dedication-title">{page.title}</h2>
            <p className="dedication-text">{page.text}</p>
            {page.year && <div className="dedication-year">{(t.madeWithLoveIn || 'صُنعت بحبّ في {year}').replace('{year}', page.year)}</div>}
            <div className="back-divider" aria-hidden="true" />
          </div>
          <span className="spread-gutter" aria-hidden="true" />
        </div>
      </div>
    );
  }

  // Open-book spread: illustration page facing text page. The image side
  // alternates per scene like a real picture book. 'text-only' scenes (unpaid
  // preview) show a blurred illustration page with the unlock CTA instead.
  const imageSide = page.sceneIndex % 2 === 0 ? 'right' : 'left';
  const imageLeaf = page.kind === 'text-only' ? (
    <div className="leaf leaf--image">
      <div className="leaf-image scene-image-blurred" aria-hidden="true" />
      <LockedTeaser />
    </div>
  ) : (
    <div className="leaf leaf--image">
      {page.imageUrl && !page.imageUrl.endsWith('placeholder-scene.svg') ? (
        <img className="leaf-image" src={resolveAsset(page.imageUrl)} alt="" />
      ) : (
        <div className="leaf-image scene-image-placeholder" />
      )}
    </div>
  );
  // An admin-set per-scene size (pt) overrides the automatic length-based class.
  // We can't drop the pt in literally — it's tuned for the full-size print panel
  // and would dwarf this shrunk-down spread; scenePreviewFontSize() maps it onto
  // the same container-query scale the auto sizes use so it stays proportional.
  // Admin-dragged position pins the text block at a leaf-relative %; the class
  // supplies position:absolute + centering transform (see App.css). Only left/top
  // are dynamic. Unpositioned scenes keep the flex-centered default.
  const blockPos = page.positioned ? { left: `${page.posX}%`, top: `${page.posY}%` } : null;
  const posClass = page.positioned ? ' scene-text-positioned' : '';
  const pStyle = {
    ...(page.fontSize ? { fontSize: scenePreviewFontSize(page.fontSize), lineHeight: 1.8 } : null),
    ...(page.align ? { textAlign: page.align } : null),
  };
  const sceneText = (
    <p
      className={`${page.fontSize ? 'scene-text' : `scene-text ${sceneTextSizeClass(page.text)}`}${page.textBubble ? '' : posClass}`.trim()}
      style={{ ...pStyle, ...(page.textBubble ? null : blockPos) }}
    >
      {isolateBidi(page.text, page.dir || 'rtl')}
    </p>
  );
  return (
    <div className="page page-book">
      <div className={`book-spread book-spread--image-${imageSide}`}>
        {imageLeaf}
        <div className="leaf leaf--text" dir={page.dir || 'rtl'}>
          {/* Hand-made off-white bubble: the oval hugs the text so it always
              contains it (admin opt-in). */}
          {page.textBubble ? (
            <span className={`bubble-wrap${posClass}`} style={blockPos || undefined}>
              <TextBubble seed={page.sceneIndex} />
              {sceneText}
            </span>
          ) : sceneText}
        </div>
        <span className="spread-gutter" aria-hidden="true" />
      </div>
    </div>
  );
}

// `font` is the story's per-story body-font key (story.storyFont). It is scoped
// to this book only — set as --story-font on the root, it overrides the global
// value index.js publishes, and omitting it leaves the global font in force.
export default function BookViewer({ pages, font, editableTitle, onTitlePosition, onNavigate }) {
  const t = useT();
  const [idx, setIdx] = useState(0);
  const [flipping, setFlipping] = useState(null);
  // Customer-dragged cover-title position (panel-relative %). null = untouched,
  // so the saved/default position wins until the customer actually drags it.
  const [titlePos, setTitlePos] = useState(null);

  // Drag the front-cover title within its board. The board (.book-board) shares
  // the print panel's aspect ratio, so a position measured as a % of the board
  // maps 1:1 onto the printed cover. translateX(-50%) (in CoverTitle) anchors x
  // as the title's centre. A move past a small threshold counts as a drag; on
  // release we persist via onTitlePosition so the placement sticks into the book.
  const startTitleDrag = (e) => {
    if (flipping || !editableTitle) return;
    const board = e.currentTarget.closest('.book-board');
    if (!board) return;
    e.preventDefault();
    const startX = e.clientX, startY = e.clientY;
    let moved = false;
    let last = null;
    const move = (ev) => {
      if (!moved && Math.abs(ev.clientX - startX) < 4 && Math.abs(ev.clientY - startY) < 4) return;
      moved = true;
      const box = board.getBoundingClientRect();
      if (!box.width || !box.height) return;
      const x = ((ev.clientX - box.left) / box.width) * 100;
      const y = ((ev.clientY - box.top) / box.height) * 100;
      last = {
        x: Math.round(Math.min(95, Math.max(5, x))),
        y: Math.round(Math.min(92, Math.max(0, y))),
      };
      setTitlePos(last);
    };
    const up = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      if (moved && last && onTitlePosition) onTitlePosition(last);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  };

  useEffect(() => {
    const onKey = (e) => {
      if (flipping) return;
      if (e.key === 'ArrowLeft') {
        if (idx >= pages.length - 1) return;
        onNavigate?.('next');
        setFlipping('next');
        window.setTimeout(() => { setIdx((i) => i + 1); setFlipping(null); }, FLIP_MS);
      } else if (e.key === 'ArrowRight') {
        if (idx <= 0) return;
        onNavigate?.('prev');
        setFlipping('prev');
        window.setTimeout(() => { setIdx((i) => i - 1); setFlipping(null); }, FLIP_MS);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [idx, flipping, pages.length, onNavigate]);

  if (!pages || pages.length === 0) return null;

  const go = (dir) => {
    if (flipping) return;
    const target = dir === 'next' ? idx + 1 : idx - 1;
    if (target < 0 || target >= pages.length) return;
    onNavigate?.(dir);
    setFlipping(dir);
    window.setTimeout(() => {
      setIdx(target);
      setFlipping(null);
    }, FLIP_MS);
  };

  const atFirst = idx === 0;
  const atLast = idx === pages.length - 1;

  const underneathIdx =
    flipping === 'next' ? idx + 1 : flipping === 'prev' ? idx - 1 : idx;
  const flipperFront = flipping === 'prev' ? pages[underneathIdx] : pages[idx];
  const flipperBack  = flipping === 'prev' ? pages[idx]            : pages[underneathIdx];

  // Small label explaining the physical-book state of the current page:
  // closed (front board) → open spreads → closed (back board).
  const currentKind = pages[underneathIdx]?.kind;
  const stateLabel =
    currentKind === 'front' ? t.bookStateClosedFront
    : currentKind === 'back' ? t.bookStateClosedBack
    : t.bookStateOpen;

  // Only the settled (non-flipping) front cover is draggable — never a page
  // mid-flip. titleDrag carries the live override + the pointer-down starter.
  const currentPage = pages[underneathIdx];
  const titleDrag = (editableTitle && !flipping && currentPage?.kind === 'front')
    ? { position: titlePos, onPointerDown: startTitleDrag }
    : null;

  return (
    <div className="book" style={storyFontCss(font) ? { '--story-font': storyFontCss(font) } : undefined}>
      <div className="book-state-chip">{stateLabel}</div>
      <div className="book-stage">
        <Page page={currentPage} titleDrag={titleDrag} />
        {flipping && (
          <div className={`book-flipper book-flipper-${flipping}`}>
            <div className="book-flipper-front"><Page page={flipperFront} /></div>
            <div className="book-flipper-back"><Page page={flipperBack} /></div>
          </div>
        )}

        <button
          type="button"
          className="book-nav book-nav-next"
          onClick={() => go('next')}
          disabled={atLast || !!flipping}
          aria-label={t.nextPage}
        >
          &#8250;
        </button>
        <button
          type="button"
          className="book-nav book-nav-prev"
          onClick={() => go('prev')}
          disabled={atFirst || !!flipping}
          aria-label={t.prevPage}
        >
          &#8249;
        </button>
      </div>

      <div className="book-progress">{idx + 1} / {pages.length}</div>
    </div>
  );
}
